/**
 * header.js — <sticky-header>
 *
 * Two independent states, both driven from one throttled scroll read:
 *
 *   is-condensed    the visitor has committed to scrolling; the header gets
 *                   shorter and translucent, but never hides. A header that
 *                   hides costs a tap every time someone reaches for the cart.
 *
 *   is-transparent  the header is currently sitting on top of hero media. It
 *                   reverts the instant it stops overlapping that media, so
 *                   light text is never stranded on a light background.
 *
 * Transparency is opt-in from the page, not assumed: a section declares
 * `data-header-overlaps` to say "I am media the header may sit on". With no
 * such section — a policy page, a search result — the header is simply solid.
 */

const CONDENSE_AT = 24;

class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.allowTransparent = this.dataset.transparent === 'true';
    this.measure();
    this.update();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    // Section re-renders in the theme editor change what the header sits on.
    document.addEventListener('shopify:section:load', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:section:load', this.onResize);
  }

  measure() {
    this.overlapTarget = this.allowTransparent
      ? document.querySelector('[data-header-overlaps]')
      : null;
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  onResize() {
    this.measure();
    this.update();
  }

  update() {
    this.classList.toggle('is-condensed', window.scrollY > CONDENSE_AT);

    // Read the boundary live rather than caching a scroll offset. A cached
    // number goes stale the moment the media above changes height — a hero
    // image finishing loading, a video swapping in, a webfont reflowing the
    // copy over it — and a stale boundary is what leaves white text stranded
    // on a white background.
    const overlapping = this.overlapTarget
      ? this.overlapTarget.getBoundingClientRect().bottom > this.offsetHeight
      : false;

    this.classList.toggle('is-transparent', overlapping);
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}

/**
 * Mobile menu disclosures. Uses <details>, so every submenu is open-able and
 * readable with no JavaScript at all; this only enforces one-open-at-a-time.
 */
document.addEventListener('toggle', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLDetailsElement)) return;
  if (!target.matches('[data-menu-disclosure]') || !target.open) return;

  target
    .closest('[data-menu-disclosure-group]')
    ?.querySelectorAll('[data-menu-disclosure][open]')
    .forEach((other) => {
      if (other !== target) other.open = false;
    });
}, true);

export default StickyHeader;
